import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'antd';
import { formatCalculatedAtTime } from '../utils/dataUpdatedAt';

interface MetricTrendPoint {
  label: string;
  value: number;
}

interface Metric {
  metricKey: string;
  valueText: string;
  rawValue?: number;
  dodChangeText?: string;
  dodChange?: number;
  severity: string;
  trend: MetricTrendPoint[];
}

interface MetricsBarProps {
  metrics: Metric[];
  calculatedAt?: string | null;
  timeZone?: string | null;
  // 断更判定小时数由后端返回，不写死在 i18n 文案里
  noUpdateThresholdHours?: number;
  loading?: boolean;
}

/**
 * 四档染色，与「历史包裹追踪」页的分档一致（后端按同一套分档算出 severity）。
 * NORMAL 是无数据态，走中性色。
 */
const SEVERITY_TEXT_COLOR: Record<string, string> = {
  EXCELLENT: 'text-green-500',
  GOOD: 'text-blue-500',
  WARNING: 'text-yellow-500',
  CRITICAL: 'text-red-500',
};

const SEVERITY_BAR_COLOR: Record<string, string> = {
  EXCELLENT: 'bg-green-300',
  GOOD: 'bg-blue-300',
  WARNING: 'bg-yellow-300',
  CRITICAL: 'bg-red-300',
};

const severityTextColor = (severity: string): string =>
  SEVERITY_TEXT_COLOR[severity] || 'text-gray-900';

const MiniBars: React.FC<{ trend: MetricTrendPoint[]; severity: string }> = ({ trend, severity }) => {
  if (trend.length === 0) return null;
  const max = Math.max(...trend.map((p) => p.value));
  const barColor = SEVERITY_BAR_COLOR[severity] || 'bg-violet-300';

  return (
    <div className="flex items-end gap-0.5 h-7 shrink-0">
      {trend.map((point, idx) => {
        const height = max > 0 ? (point.value / max) * 100 : 0;
        return (
          <div
            key={idx}
            className={`w-1.5 rounded-sm ${barColor}`}
            style={{ height: `${Math.max(height, 12)}%` }}
            title={`${point.label}: ${point.value}`}
          />
        );
      })}
    </div>
  );
};

const MetricsBar: React.FC<MetricsBarProps> = ({
  metrics,
  calculatedAt,
  timeZone,
  noUpdateThresholdHours,
  loading,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="px-6 py-3 border-b border-violet-200">
        <Skeleton active paragraph={{ rows: 1 }} />
      </div>
    );
  }

  const metricLabels: Record<string, string> = {
    PICKING_RATE: String(t('workbench.metrics.pickingRate')),
    DELIVERY_RATE: String(t('workbench.metrics.deliveryRate')),
    RETURN_RATE: String(t('workbench.metrics.returnRate')),
    NO_UPDATE: String(t('workbench.metrics.noUpdate')),
  };

  return (
    <div className="flex items-center divide-x divide-violet-200 border-b border-violet-200">
      {metrics.map((metric) => {
        const isNoUpdate = metric.metricKey === 'NO_UPDATE';
        const valueColor = severityTextColor(metric.severity);
        // 环比暂时下线：数仓 SLA 表按日聚合，今日累计率 vs 昨日全天最终率的比较无参考意义，
        // 待数仓侧提供小时切片后按同窗口对齐重新出。字段 dodChangeText/dodChange 仍从后端拉，纯前端不渲染

        return (
          // 断更格还要塞「数据更新于 + 时分秒」，等分四份会把它挤成两行，故多分一点宽度
          <div
            key={metric.metricKey}
            className={`min-w-0 pl-2 pr-6 py-3 first:pl-0 last:pr-0 ${
              isNoUpdate ? 'flex-[1.5]' : 'flex-1'
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="text-sm text-gray-600 shrink-0">
                  {metricLabels[metric.metricKey] || metric.metricKey}
                </span>
                <span className={`text-xl font-bold ${valueColor}`}>
                  {metric.valueText}
                </span>
                {isNoUpdate && (
                  <span className="text-xs text-gray-500 shrink-0">
                    {String(t('workbench.metrics.noUpdateSuffix', { hours: noUpdateThresholdHours }))}
                  </span>
                )}
              </div>

              {isNoUpdate
                ? calculatedAt && (
                  <span className="text-xs text-red-500 shrink-0 text-right leading-tight">
                    {String(t('workbench.dataUpdated'))}
                    <br />
                    {formatCalculatedAtTime(calculatedAt, timeZone) || '—'}
                  </span>
                )
                : <MiniBars trend={metric.trend} severity={metric.severity} />}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MetricsBar;
